import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  selectHasSupportedAdvisorCapability,
  selectPersonaKey,
  selectTokenUserId,
} from "../store/auth-slice";
import { selectAdvisorProfile } from "../store/advisorContext-slice";
import { fetchAdvisorProfile } from "../store/advisorContext-actions";

// ITAM-2887: headless. Mounted next to the other global managers so the
// advisor context is loaded on fresh login and on session restore,
// independently of which route the advisor lands on. Ported from the dev
// branch's ConsultPharmacyContextLoader minus the pair and detail branches.
const AdvisorContextLoader = () => {
  const dispatch = useDispatch();
  const hasCapability = useSelector(selectHasSupportedAdvisorCapability);
  const consultantId = useSelector(selectTokenUserId);
  const personaKey = useSelector(selectPersonaKey);
  const profile = useSelector(selectAdvisorProfile);
  const status = useSelector(
    (state: RootState) => state.advisorContext.status,
  );

  const armed = hasCapability && consultantId != null;

  // (a) initial load: fresh login, session restore, AND the persona-changing
  // token refresh. `personaKey` is a dependency because `auth/personaExit`
  // resets the context slice while `armed` and `consultantId` may survive.
  useEffect(() => {
    if (!armed || consultantId == null) {
      return;
    }
    const controller = new AbortController();
    dispatch<any>(fetchAdvisorProfile(consultantId, controller.signal));
    return () => controller.abort();
  }, [dispatch, armed, consultantId, personaKey]);

  // (c) window focus retries a FIRST load that failed outright (non-404).
  // `error` is only reachable with nothing loaded, and no other automatic
  // trigger can retry it. NEVER armed for `noProfile`: that is a
  // server-confirmed 404, and refetching there would swap the create form
  // for the loading placeholder under the click that follows the focus.
  const loadFailed =
    armed && consultantId != null && profile == null && status === "error";
  useEffect(() => {
    if (!loadFailed) {
      return;
    }
    const onFocus = () => {
      if (consultantId != null) {
        dispatch<any>(fetchAdvisorProfile(consultantId));
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [dispatch, loadFailed, consultantId]);

  return null;
};

export default AdvisorContextLoader;
